import clsx from "clsx";
import { FC, useEffect } from "react";

import { AIChatPanel } from "./ai/components/AIChatPanel";
import {
  MOBILE_OVERLAY_ROUTES,
  MOBILE_OVERLAY_ROUTE_QUERY_PARAM,
  MobileOverlayRouteType,
} from "./constants";
import { ClassChatSidebarPanel } from "./materials/section/ClassChatSidebarPanel";
import { SectionUsersSidebarPanel } from "./materials/section/SectionUsersSidebarPanel";
import { ManageSectionPagesMobilePanel } from "./materials/section/content/pages/ManageSectionPagesMobilePanel";
import { PlannerSidebarPanel } from "./materials/section/planner/PlannerSidebarPanel";
import { InstructorNotesSidebarPanel } from "./materials/sidebar/InstructorNotesSidebarPanel";
import { UserProfileSidebarPanel } from "./materials/sidebar/UserProfileSidebarPanel";

import { MobileMainMenu } from "components/menus/MobileMainMenu";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useAppSelector } from "store/index";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";
import { useAv } from "utils/av";
import { useQueryParamsManager } from "utils/routing";

const MOBILE_OVERLAY_ROUTE_COMPONENTS: Record<MobileOverlayRouteType, FC> = {
  menu: MobileMainMenu,
  users: SectionUsersSidebarPanel,
  profile: UserProfileSidebarPanel,
  instructor_notes: InstructorNotesSidebarPanel,
  planner: PlannerSidebarPanel,
  ai: AIChatPanel,
  chat: ClassChatSidebarPanel,
  manage_pages: ManageSectionPagesMobilePanel,
};

// Routes that have no desktop rendering path, so the overlay param should be cleared
// once the viewport grows past mobile.
const MOBILE_ONLY_OVERLAY_ROUTES: MobileOverlayRouteType[] = ["profile", "planner", "manage_pages"];

/**
 * This component displays mobile-only routes (like the mobile menu, sidebar views, etc.)
 * over the main content. It needs to be inside a relative container.
 */
export const MobileRoutingOverlay = () => {
  const { getQueryParam, deleteQueryParams } = useQueryParamsManager();
  const isMdOrLarger = useIsMdOrLarger();

  const { isInVirtualClass } = useAv();
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const isInVirtualClassOrObserving = isInVirtualClass || isObservingClass;

  const mobileOverlayRoute = getQueryParam(
    MOBILE_OVERLAY_ROUTE_QUERY_PARAM,
  ) as MobileOverlayRouteType;

  // Clear the overlay param for mobile-only routes once the viewport is md+, since those
  // panels have no desktop rendering path and the stale param can affect desktop layout.
  useEffect(() => {
    if (isMdOrLarger && MOBILE_ONLY_OVERLAY_ROUTES.includes(mobileOverlayRoute)) {
      deleteQueryParams([MOBILE_OVERLAY_ROUTE_QUERY_PARAM]);
    }
  }, [isMdOrLarger, mobileOverlayRoute, deleteQueryParams]);

  if (!mobileOverlayRoute || !MOBILE_OVERLAY_ROUTES.includes(mobileOverlayRoute)) {
    return null;
  }

  const isMobileMainMenuOpen = mobileOverlayRoute === "menu";
  const MobileOverlayComponent = MOBILE_OVERLAY_ROUTE_COMPONENTS[mobileOverlayRoute];

  // NOTE: the mobile menu is a special case because its height extends to the top and bottom of the screen,
  // covering the navbar and footer, but its width is fixed to 340px so the underlying content is still visible
  // through the modal backdrop.
  return (
    <>
      {isMobileMainMenuOpen && (
        <div
          className="modal-backdrop"
          onClick={() => deleteQueryParams([MOBILE_OVERLAY_ROUTE_QUERY_PARAM])}
        />
      )}
      <div
        className={clsx("absolute z-[100] bg-white data-[dark]:bg-black-tint-15 lg:!hidden", {
          "page content-sidebar-panel inset-0 bottom-0": !isMobileMainMenuOpen,
          "-top-[calc(var(--navbar-height)+var(--participant-bar-height))] right-0 h-svh w-[340px]":
            isMobileMainMenuOpen,
          "!py-0": isInVirtualClassOrObserving,
        })}
        data-dark={isInVirtualClassOrObserving || undefined}
      >
        <MobileOverlayComponent />
      </div>
    </>
  );
};
